<div class="import-review">
  <!-- Header -->
  <div class="import-header">
    <h2>
      @if (currentStep() === 'customer') {
        Select Customer
      } @else {
        Review & Import Bills
      }
    </h2>
    <button class="close-btn" (click)="cancel()" title="Close">
      <i class="material-symbols-rounded">close</i>
    </button>
  </div>

  <!-- Step Indicator (only when customer step is needed) -->
  @if (needsCustomerStep()) {
    <div class="step-indicator">
      <div class="step" [class.active]="currentStep() === 'customer'" [class.completed]="currentStep() === 'review'">
        <div class="step-circle">
          @if (currentStep() === 'review') {
            <i class="material-symbols-rounded">check</i>
          } @else {
            <span>1</span>
          }
        </div>
        <span class="step-label">Customer</span>
      </div>
      <div class="step-connector" [class.completed]="currentStep() === 'review'"></div>
      <div class="step" [class.active]="currentStep() === 'review'">
        <div class="step-circle">
          <span>2</span>
        </div>
        <span class="step-label">Review & Import</span>
      </div>
    </div>
  }

  <!-- Loading State -->
  @if (isLoading()) {
    <div class="loading-container">
      <mat-spinner diameter="48"></mat-spinner>
      <p>{{ isMatching() ? 'Finding matches...' : 'Importing bills...' }}</p>
    </div>
  }

  <!-- Error State -->
  @if (error() && !isLoading()) {
    <div class="error-container">
      <i class="material-symbols-rounded">error</i>
      <span>{{ error() }}</span>
      <div class="error-actions">
        <button class="btn-secondary" (click)="cancel()">Cancel</button>
        @if (currentStep() === 'customer') {
          <button class="btn-primary" (click)="runMatch()">Retry</button>
        } @else {
          <button class="btn-primary" (click)="runMatch(selectedCustomerId() != null ? +selectedCustomerId()! : null)">Retry</button>
        }
      </div>
    </div>
  }

  <!-- ======================================================================= -->
  <!-- STEP 1: Customer Selection                                              -->
  <!-- ======================================================================= -->
  @if (currentStep() === 'customer' && !isLoading() && !error()) {
    <div class="customer-step">
      @if (customerOptions().length > 1) {
        <p class="step-description">We found potential matches based on the legal entities in your bills.</p>
      } @else {
        <p class="step-description">No existing customer matches found. A new customer will be created.</p>
      }

      <div class="customer-cards">
        @for (option of customerOptions(); track option.value) {
          <div class="customer-card-wrapper">
            <button
              class="customer-card"
              [class.selected]="selectedCustomerId() === option.value"
              [class.create-new]="option.isCreateNew"
              (click)="selectCustomer(option.value, option.isCreateNew ? newCustomerPreview().suggestedName : option.label)">
              <div class="card-radio">
                <mat-radio-button
                  [checked]="selectedCustomerId() === option.value"
                  [value]="option.value"
                  disableRipple
                ></mat-radio-button>
              </div>
              <div class="card-content">
                @if (option.isCreateNew) {
                  <div class="card-icon create-new-icon">
                    <i class="material-symbols-rounded">add_business</i>
                  </div>
                  <div class="card-detail">
                    <span class="card-name">Create New Customer</span>
                    <span class="card-sub">All entities will be created fresh</span>
                  </div>
                } @else {
                  <div class="card-icon match-icon">
                    <i class="material-symbols-rounded">account_balance</i>
                  </div>
                  <div class="card-detail">
                    <span class="card-name">{{ option.label }}</span>
                    <span class="card-sub">
                      <span class="match-score">{{ (option.score! * 100) | number:'1.0-0' }}% match</span>
                    </span>
                  </div>
                }
              </div>
            </button>

            <!-- Expanded detail panel for the selected option -->
            @if (selectedCustomerId() === option.value) {
              <div class="card-detail-panel">
                @if (option.isCreateNew) {
                  <!-- New customer preview -->
                  <div class="detail-row">
                    <i class="material-symbols-rounded detail-icon">badge</i>
                    <span class="detail-label">Name</span>
                    <span class="detail-value">{{ newCustomerPreview().suggestedName }}</span>
                  </div>
                  @if (newCustomerPreview().billingAddress) {
                    <div class="detail-row">
                      <i class="material-symbols-rounded detail-icon">mail</i>
                      <span class="detail-label">Billing Address</span>
                      <span class="detail-value">{{ newCustomerPreview().billingAddress }}</span>
                    </div>
                  }
                  <div class="detail-row">
                    <i class="material-symbols-rounded detail-icon">account_balance</i>
                    <span class="detail-label">Legal Entities</span>
                    <span class="detail-value">
                      {{ newCustomerPreview().legalEntityNames.join(', ') }}
                    </span>
                  </div>
                  <div class="detail-counts">
                    <span class="detail-count">
                      <i class="material-symbols-rounded">home_work</i>
                      {{ newCustomerPreview().serviceAddressCount }} {{ newCustomerPreview().serviceAddressCount === 1 ? 'address' : 'addresses' }}
                    </span>
                    <span class="detail-count">
                      <i class="material-symbols-rounded">speed</i>
                      {{ newCustomerPreview().meterCount }} {{ newCustomerPreview().meterCount === 1 ? 'meter' : 'meters' }}
                    </span>
                    <span class="detail-count">
                      <i class="material-symbols-rounded">receipt_long</i>
                      {{ newCustomerPreview().billCount }} {{ newCustomerPreview().billCount === 1 ? 'bill' : 'bills' }}
                    </span>
                  </div>
                } @else {
                  <!-- Existing customer match reasons -->
                  @if (option.matchReasons && option.matchReasons.length > 0) {
                    <div class="match-reasons">
                      <span class="reasons-label">Matched because:</span>
                      @for (reason of option.matchReasons; track reason) {
                        <span class="reason-chip">
                          <i class="material-symbols-rounded">check_circle</i>
                          {{ reason }}
                        </span>
                      }
                    </div>
                  }
                }
              </div>
            }
          </div>
        }
      </div>

      <!-- Customer Step Actions -->
      <div class="actions-bar">
        <button class="btn-secondary" (click)="cancel()">Cancel</button>
        <button
          class="btn-primary"
          (click)="confirmCustomer()"
          [disabled]="selectedCustomerId() === undefined">
          Continue
          <i class="material-symbols-rounded">arrow_forward</i>
        </button>
      </div>
    </div>
  }

  <!-- ======================================================================= -->
  <!-- STEP 2: Entity Review & Import                                          -->
  <!-- ======================================================================= -->
  @if (currentStep() === 'review' && !isLoading() && !error() && matchResponse()) {
    <!-- Customer breadcrumb (when coming from customer step) -->
    @if (needsCustomerStep()) {
      <div class="customer-breadcrumb">
        <button class="breadcrumb-back" (click)="goBackToCustomer()" title="Change customer">
          <i class="material-symbols-rounded">arrow_back</i>
        </button>
        <i class="material-symbols-rounded breadcrumb-icon">account_balance</i>
        <span class="breadcrumb-label">Customer:</span>
        <span class="breadcrumb-value">{{ selectedCustomerLabel() }}</span>
        @if (selectedCustomerId() === null) {
          <span class="badge new">new</span>
        }
      </div>
    }

    <!-- Stats Summary -->
    <div class="stats-bar">
      <div class="stat">
        <i class="material-symbols-rounded stat-icon le">account_balance</i>
        <div class="stat-detail">
          <span class="stat-value">{{ stats().legalEntities.total }}</span>
          <span class="stat-label">Legal {{ stats().legalEntities.total === 1 ? 'Entity' : 'Entities' }}</span>
        </div>
        <span class="stat-breakdown">
          @if (stats().legalEntities.new > 0) {
            <span class="badge new">{{ stats().legalEntities.new }} new</span>
          }
          @if (stats().legalEntities.existing > 0) {
            <span class="badge existing">{{ stats().legalEntities.existing }} existing</span>
          }
        </span>
      </div>
      <div class="stat">
        <i class="material-symbols-rounded stat-icon sa">home_work</i>
        <div class="stat-detail">
          <span class="stat-value">{{ stats().serviceAddresses.total }}</span>
          <span class="stat-label">Service {{ stats().serviceAddresses.total === 1 ? 'Address' : 'Addresses' }}</span>
        </div>
        <span class="stat-breakdown">
          @if (stats().serviceAddresses.new > 0) {
            <span class="badge new">{{ stats().serviceAddresses.new }} new</span>
          }
          @if (stats().serviceAddresses.existing > 0) {
            <span class="badge existing">{{ stats().serviceAddresses.existing }} existing</span>
          }
        </span>
      </div>
      <div class="stat">
        <i class="material-symbols-rounded stat-icon meter">speed</i>
        <div class="stat-detail">
          <span class="stat-value">{{ stats().meters.total }}</span>
          <span class="stat-label">{{ stats().meters.total === 1 ? 'Meter' : 'Meters' }}</span>
        </div>
        <span class="stat-breakdown">
          @if (stats().meters.new > 0) {
            <span class="badge new">{{ stats().meters.new }} new</span>
          }
          @if (stats().meters.existing > 0) {
            <span class="badge existing">{{ stats().meters.existing }} existing</span>
          }
        </span>
      </div>
      <div class="stat">
        <i class="material-symbols-rounded stat-icon bill">receipt_long</i>
        <div class="stat-detail">
          <span class="stat-value">{{ stats().bills.total }}</span>
          <span class="stat-label">{{ stats().bills.total === 1 ? 'Bill' : 'Bills' }}</span>
        </div>
        @if (stats().bills.duplicates > 0) {
          <span class="stat-breakdown">
            <span class="badge warning">{{ stats().bills.duplicates }} duplicate</span>
          </span>
        }
      </div>
    </div>

    <!-- Entity Tree -->
    <div class="entity-tree">
      @for (len of matchResponse()!.legalEntities; track len.groupId) {
        <div class="entity-card">
          <!-- Legal Entity -->
          <div class="entity-row len-row">
            <div class="entity-icon">
              <i class="material-symbols-rounded">account_balance</i>
            </div>
            <div class="entity-info">
              <span class="entity-type">Legal Entity</span>
              <span class="entity-name">{{ getOriginalName(len.groupId) }}</span>
            </div>
            <div class="entity-action">
              <select
                class="match-select"
                [ngModel]="getSelection(len.groupId)"
                (ngModelChange)="updateSelection(len.groupId, $event)">
                @for (option of getOptions(len.match, len.alternatives, getOriginalName(len.groupId)); track option.value) {
                  <option [ngValue]="option.value">{{ option.label }}</option>
                }
              </select>
            </div>
          </div>

          <!-- Service Addresses -->
          @for (sa of len.serviceAddresses; track sa.groupId) {
            <div class="entity-row sa-row">
              <div class="entity-icon">
                <i class="material-symbols-rounded">home_work</i>
              </div>
              <div class="entity-info">
                <span class="entity-type">Service Address</span>
                <span class="entity-name">{{ getOriginalName(sa.groupId) }}</span>
              </div>
              <div class="entity-action">
                <select
                  class="match-select"
                  [ngModel]="getSelection(sa.groupId)"
                  (ngModelChange)="updateSelection(sa.groupId, $event)">
                  @for (option of getOptions(sa.match, sa.alternatives, getOriginalName(sa.groupId)); track option.value) {
                    <option [ngValue]="option.value">{{ option.label }}</option>
                  }
                </select>
              </div>
            </div>

            <!-- Meters -->
            @for (meter of sa.meters; track meter.groupId) {
              <div class="entity-row meter-row">
                <div class="entity-icon">
                  <i class="material-symbols-rounded">speed</i>
                </div>
                <div class="entity-info">
                  <span class="entity-type">Meter</span>
                  <span class="entity-name">{{ getOriginalName(meter.groupId) }}</span>
                  @if (meter.utility) {
                    <span class="utility-badge">{{ meter.utility.utilityName }}</span>
                  }
                </div>
                <div class="entity-action">
                  <select
                    class="match-select"
                    [ngModel]="getSelection(meter.groupId)"
                    (ngModelChange)="updateSelection(meter.groupId, $event)">
                    @for (option of getOptions(meter.match, meter.alternatives, getOriginalName(meter.groupId)); track option.value) {
                      <option [ngValue]="option.value">{{ option.label }}</option>
                    }
                  </select>
                </div>
              </div>

              <!-- Bills under this meter -->
              <div class="bills-list">
                @for (bill of meter.bills; track bill.groupId) {
                  <div class="bill-row" [class.duplicate]="bill.duplicate">
                    <i class="material-symbols-rounded">receipt</i>
                    <span class="bill-dates">
                      {{ formatDate(matchRequest()!.legalEntities[0].serviceAddresses[0].meters[0].bills[0].billStartDate) }}
                    </span>
                    @if (bill.duplicate) {
                      <span class="duplicate-badge">
                        <i class="material-symbols-rounded">warning</i>
                        Duplicate
                      </span>
                    }
                  </div>
                }
              </div>
            }
          }
        </div>
      }
    </div>

    <!-- Actions -->
    <div class="actions-bar">
      @if (needsCustomerStep()) {
        <button class="btn-secondary" (click)="goBackToCustomer()">
          <i class="material-symbols-rounded">arrow_back</i>
          Back
        </button>
      } @else {
        <button class="btn-secondary" (click)="cancel()">Cancel</button>
      }
      <button class="btn-primary" (click)="runImport()" [disabled]="isImporting()">
        @if (isImporting()) {
          <mat-spinner diameter="18"></mat-spinner>
        }
        <i class="material-symbols-rounded">cloud_upload</i>
        Import {{ stats().bills.total }} Bill{{ stats().bills.total === 1 ? '' : 's' }}
      </button>
    </div>
  }
</div>
